import google, { canvasProjection, get, OverlayRegistry, OverlayType, toLatLng } from "@churchofjesuschrist/maps-google";
import { convertUnit, crossesAntiMeridian, isPointInRange, pushLongitude, roundTo, toDecimalDegrees, toMercatorMeters } from "@churchofjesuschrist/maps-math";
import { clonePath, clonePaths, closeLoops, containsBounds, getBoundsHeight, getBoundsPath, getBoundsWidth, getCirclePath, getClosestPositionOnCircle, getClosestPositionOnPaths, getPathsBounds, getPolygonArea, getPolygonPerimeter, isClockwise, isLocationBetween, isPointBetween } from "@churchofjesuschrist/maps-google-math";
import { AreaUnitType, LengthUnitType, Position } from "@churchofjesuschrist/maps-models";
import { defaultEmpty, distinct, isArray, isDefined, isEmpty, isNumber, isObject, isObjectNotEmpty, toArray } from "@churchofjesuschrist/maps-utils";
import { degreeNearlyEqualsTolerance, mapExtentProjected } from "@churchofjesuschrist/maps-constants";
export const getOverlayProperties = (overlay, ignore = null, more = null) => {
  let type = getOverlayType(overlay),
    properties;
  switch (type) {
    case OverlayType.MARKER:
      properties = ["position", "icon", "opacity", "shape", "zIndex"];
      break;
    case OverlayType.CIRCLE:
      properties = ["center", "radius", "fillColor", "fillOpacity", "strokeColor", "strokeOpacity", "strokeWeight", "zIndex"];
      break;
    case OverlayType.RECTANGLE:
      properties = ["bounds", "fillColor", "fillOpacity", "strokeColor", "strokeOpacity", "strokeWeight", "zIndex"];
      break;
    case OverlayType.POLYGON:
      properties = ["paths", "fillColor", "fillOpacity", "strokeColor", "strokeOpacity", "strokeWeight", "zIndex"];
      break;
    case OverlayType.POLYLINE:
      properties = ["path", "geodesic", "icons", "strokeColor", "strokeOpacity", "strokeWeight", "zIndex"];
      break;
    default:
      properties = ["zIndex"];
      break;
  }
  if (more) {
    properties = distinct([...properties, ...toArray(more)]);
  }
  let results = properties.filter(key => !ignore || !ignore.includes(key)).reduce((options, key) => {
    let value = get(overlay, key);
    if (isDefined(value)) {
      options[key] = value;
    }
    return options;
  }, {});
  if (overlay.getOverlayProperties) {
    let customProperties = overlay.getOverlayProperties();
    if (ignore) {
      ignore.forEach(key => {
        delete customProperties[key];
      });
    }
    Object.assign(results, customProperties);
  }
  return results;
};
export const getOverlayVisualProperties = (overlay, more = null) => {
  const overlayGeometryProperties = ["position", "center", "radius", "bounds", "paths", "path"];
  return getOverlayProperties(overlay, overlayGeometryProperties, more);
};
export const cloneOverlay = overlay => {
  let type = getOverlayType(overlay),
    properties = getOverlayProperties(overlay);
  switch (type) {
    case OverlayType.MARKER:
      return new google.maps.Marker(properties);
    case OverlayType.CIRCLE:
      return new google.maps.Circle(properties);
    case OverlayType.RECTANGLE:
      return new google.maps.Rectangle(properties);
    case OverlayType.POLYGON:
      {
        let paths = properties.paths;
        if (paths) {
          paths = clonePaths(paths);
        }
        return new google.maps.Polygon({
          ...properties,
          paths
        });
      }
    case OverlayType.POLYLINE:
      {
        let path = properties.path;
        if (path) {
          path = clonePath(path);
        }
        return new google.maps.Polyline({
          ...properties,
          path
        });
      }
    default:
      //TODO
      break;
  }
};
export const deserializeProperties = properties => {
  if (properties) {
    properties = Object.entries(properties).reduce((newProps, [key, value]) => {
      if (isObject(value) && !isArray(value)) {
        if (isNumber(value.x) && isNumber(value.y)) {
          newProps[key] = new google.maps.Point(value.x, value.y);
        } else if (isNumber(value.width) && isNumber(value.height)) {
          newProps[key] = new google.maps.Size(value.width, value.height);
        } else {
          newProps[key] = deserializeProperties(value);
        }
      } else {
        newProps[key] = value;
      }
      return newProps;
    }, {});
  }
  return properties;
};
export const getOverlayType = overlay => {
  if (google.maps?.Circle) {
    if (overlay instanceof google.maps.Circle) {
      return OverlayType.CIRCLE;
    }
    if (overlay instanceof google.maps.Rectangle) {
      return OverlayType.RECTANGLE;
    }
    if (overlay instanceof google.maps.Polygon) {
      return OverlayType.POLYGON;
    }
    if (overlay instanceof google.maps.Polyline) {
      return OverlayType.POLYLINE;
    }
    if (overlay instanceof google.maps.Marker) {
      return OverlayType.MARKER;
    }
    let type = OverlayRegistry.getType(overlay);
    if (type) {
      return type;
    }
    if (overlay instanceof google.maps.OverlayView) {
      return OverlayType.CUSTOM;
    }
  }
  return null;
};
export const isOverlay = obj => {
  return !!getOverlayType(obj);
};
export const overlayLoops = overlay => {
  let type = getOverlayType(overlay);
  return type === OverlayType.POLYGON || type === OverlayType.RECTANGLE || type === OverlayType.CIRCLE;
};
export const getOverlayBounds = overlay => {
  let bounds = null,
    type = getOverlayType(overlay);
  switch (type) {
    case OverlayType.CIRCLE:
    case OverlayType.RECTANGLE:
      bounds = overlay.getBounds();
      break;
    case OverlayType.POLYGON:
      bounds = getPathsBounds(overlay.getPaths());
      break;
    case OverlayType.POLYLINE:
      bounds = getPathsBounds(overlay.getPath());
      break;
    case OverlayType.MARKER:
      bounds = getMarkerBounds(overlay);
      break;
    case OverlayType.CUSTOM:
      if (overlay.getBounds) {
        bounds = overlay.getBounds();
      }
      break;
  }
  return bounds;
};
export const getMarkerBounds = marker => {
  let bounds = null,
    position = marker.getPosition(),
    projection = canvasProjection(marker);
  if (position && projection) {
    let size = null,
      anchor = null;
    if (marker instanceof google.maps.Marker) {
      //TODO: get size and anchor
    } else {
      if (marker.getBounds) {
        return marker.getBounds();
      }
      if (marker.getIconSize) {
        size = marker.getIconSize();
      }
      if (marker.getIconAnchor) {
        anchor = marker.getIconAnchor();
      }
    }
    if (size) {
      let point = projection.fromLatLngToDivPixel(position);
      anchor = anchor || {
        x: size.width / 2,
        y: size.height
      };
      let top = point.y - anchor.y,
        bottom = point.y + (size.height - anchor.y),
        left = point.x - anchor.x,
        right = point.x + (size.width - anchor.x);
      let ne = projection.fromDivPixelToLatLng(new google.maps.Point(right, top)),
        sw = projection.fromDivPixelToLatLng(new google.maps.Point(left, bottom));
      bounds = new google.maps.LatLngBounds(sw, ne);
    }
  }
  return bounds;
};
export const getOverlayPaths = (overlay, closePaths = false) => {
  let paths = null,
    path = null,
    type = getOverlayType(overlay);
  switch (type) {
    case OverlayType.CIRCLE:
      path = getCirclePath(overlay);
      break;
    case OverlayType.RECTANGLE:
      path = getBoundsPath(overlay.getBounds());
      break;
    case OverlayType.POLYGON:
      paths = overlay.getPaths();
      break;
    case OverlayType.POLYLINE:
      path = overlay.getPath();
      break;
  }
  if (path) {
    paths = new google.maps.MVCArray([path]);
  }
  if (closePaths) {
    paths = closeLoops(paths);
  }
  return paths;
};
export const getClosestPositionOnOverlay = (overlay, position) => {
  let closest = null,
    type = getOverlayType(overlay);
  switch (type) {
    case OverlayType.CIRCLE:
      closest = getClosestPositionOnCircle(overlay, position);
      break;
    case OverlayType.RECTANGLE:
    case OverlayType.POLYGON:
    case OverlayType.POLYLINE:
      {
        let paths = getOverlayPaths(overlay);
        closest = getClosestPositionOnPaths(paths, position);
        break;
      }
  }
  return closest;
};
export const getClosestOverlay = (overlays, position) => {
  let closest = null,
    overlay = null;
  overlays.forEach(current => {
    let closestPoint = getClosestPositionOnOverlay(current, position);
    if (closest === null || closest.distance > closestPoint.distance) {
      closest = closestPoint;
      overlay = current;
    }
  });
  return {
    overlay,
    ...closest
  };
};
export const containsOverlay = (overlay, polygon) => {
  let overlayBounds = getOverlayBounds(overlay),
    polygonBounds = getOverlayBounds(polygon);
  if (containsBounds(polygonBounds, overlayBounds)) {
    let paths = getOverlayPaths(overlay).getArray();
    if (paths.length) {
      for (let p = 0; p < paths.length; p++) {
        let path = paths[p].getArray();
        if (path.length > 1) {
          for (let v = 0; v < path.length - 1; v++) {
            let vertexA = path[v],
              vertexB = path[v + 1];
            // check if vertices are inside the polygon
            if ((v !== 0 || containsLocationOrNearlyOnEdge(vertexA, polygon)) && containsLocationOrNearlyOnEdge(vertexB, polygon)) {
              let lineSegment = new google.maps.Polyline({
                  path: [vertexA, vertexB]
                }),
                intersections = getIntersections(polygon, lineSegment);
              if (intersections.length > 1) {
                // check if midpoints between intersections are outside polygon
                for (let i = 0; i < intersections.length - 1; i++) {
                  let midpoint = google.maps.geometry.spherical.interpolate(intersections[i], intersections[i + 1], 0.5);
                  if (!containsLocationOrNearlyOnEdge(midpoint, polygon)) {
                    return false;
                  }
                }
              }
            } else {
              return false;
            }
          }
        } else if (path.length === 1 && !containsLocationOrNearlyOnEdge(path[0], polygon)) {
          return false;
        }
      }
      return true;
    }
  }
  return false;
};
export const isLocationOnPixelEdge = (point, overlay, width, ref = null) => {
  let map = null,
    projection = null,
    zoom = null;
  if (ref) {
    if (ref instanceof google.maps.Map) {
      map = ref;
    } else if (ref.fromLatLngToContainerPixel) {
      projection = ref;
    } else {
      zoom = ref;
    }
  } else if (overlay.getMap) {
    map = overlay.getMap();
  }
  if (map) {
    projection = canvasProjection(map);
    zoom = map.getZoom();
  }
  let w = isEmpty(width) ? Math.min(overlay.get("strokeWeight") || 4, 32) : Math.max(width, 1);
  let type = getOverlayType(overlay.type);
  if (projection) {
    let closest;
    if (type === OverlayType.MARKER) {
      closest = overlay.getPosition();
    } else if (overlay instanceof google.maps.LatLng) {
      closest = overlay;
    } else {
      closest = getClosestPositionOnOverlay(overlay, point).position;
    }
    let pointA = projection.fromLatLngToContainerPixel(point),
      pointB = projection.fromLatLngToContainerPixel(closest),
      size = w / 2;
    return isPointInRange(pointA, pointB, new google.maps.Size(size, size));
  } else {
    console.warn("No projection for calculating isLocationOnPixelEdge, degrading to isLocationOnEdge.");
    let z = defaultEmpty(zoom, 20),
      tolerance = 180.0 / (256 * Math.pow(2, z)) * w;
    //console.log(z,w,tolerance, Math.pow(z, -(z / 5)));

    if (type === OverlayType.MARKER || overlay instanceof google.maps.LatLng) {
      let position = overlay.getPosition ? overlay.getPosition() : overlay;
      overlay = new google.maps.Polyline({
        path: [position]
      });
    } else if (type === OverlayType.CIRCLE || type === OverlayType.RECTANGLE) {
      let paths = getOverlayPaths(overlay);
      overlay = new google.maps.Polygon({
        paths
      });
    }
    return google.maps.geometry.poly.isLocationOnEdge(point, overlay, tolerance);
  }
};
export const containsLocationOrNearlyOnEdge = (point, polygon) => {
  return google.maps.geometry.poly.containsLocation(point, polygon) || google.maps.geometry.poly.isLocationOnEdge(point, polygon, degreeNearlyEqualsTolerance);
};
export const getPolysWithEdges = (polys, points, tolerance = undefined) => {
  let results = [];
  let ploysList = toArray(polys),
    pointsList = toArray(points);
  for (let i = 0; i < ploysList.length; i++) {
    let shape = ploysList[i],
      valid = false;
    for (let j = 0; j < pointsList.length; j++) {
      valid = google.maps.geometry.poly.isLocationOnEdge(pointsList[j], shape, tolerance);
      if (!valid) {
        break;
      }
    }
    if (valid) {
      results.push(shape);
    }
  }
  return results;
};
export const hasIntersections = (shapeA, shapeB, exclusions) => {
  return !!getIntersections(shapeA, shapeB, 1, exclusions).length;
};
export const getIntersections = (shapeA, shapeB, max, exclusions) => {
  let intersections = [],
    treeA = getShapeTree(shapeA),
    treeB = getShapeTree(shapeB);
  for (let tA = 0; tA < treeA.length; tA++) {
    let boxA = treeA[tA];
    for (let tB = 0; tB < treeB.length; tB++) {
      let boxB = treeB[tB];
      if (boxA.extent.intersects(boxB.extent)) {
        let pathA = boxA.segment,
          pathB = boxB.segment,
          countA = pathA.length - 1,
          countB = pathB.length - 1;
        for (let a = 0; a < countA; a++) {
          let a1 = pathA[a],
            a2 = pathA[a + 1];
          for (let b = 0; b < countB; b++) {
            let b1 = pathB[b],
              b2 = pathB[b + 1];
            let results = getMercatorIntersectingPositions(a1, a2, b1, b2, a, countA, b, countB);
            for (let i = 0; i < results.length; i++) {
              let intersection = results[i];
              if ((!exclusions || !containsPosition(exclusions, intersection)) && !containsPosition(intersections, intersection)) {
                intersections.push(intersection);
                if (max && intersections.length >= max) {
                  return intersections;
                }
              }
            }
          }
        }
      }
    }
  }
  return intersections;
};
export const containsPosition = (list, point) => {
  for (let i = 0; i < list.length; i++) {
    if (Position.nearlyEquals(list[i], point)) {
      return true;
    }
  }
  return false;
};
export const getMercatorIntersectingPositions = (a1, a2, b1, b2, a, countA, b, countB) => {
  let intersections = [];
  let positionA1 = a1,
    positionA2 = a2,
    positionB1 = b1,
    positionB2 = b2;

  // if crosses 180 line
  let crossesA = crossesAntiMeridian(positionA1, positionA2),
    crossesB = crossesAntiMeridian(positionB1, positionB2);
  if (crossesA || crossesB) {
    positionA1 = pushLongitude(positionA1);
    positionA2 = pushLongitude(positionA2);
    positionB1 = pushLongitude(positionB1);
    positionB2 = pushLongitude(positionB2);
  }
  let pointA1 = toMercatorMeters(positionA1),
    pointA2 = toMercatorMeters(positionA2),
    pointB1 = toMercatorMeters(positionB1),
    pointB2 = toMercatorMeters(positionB2);
  if (a === 0 && !containsPosition(intersections, a1) && isLocationBetween(positionA1, positionB1, positionB2)) {
    intersections.push(a1);
  }
  if (b === 0 && !containsPosition(intersections, b1) && isLocationBetween(positionB1, positionA1, positionA2)) {
    intersections.push(b1);
  }
  let determinate = (pointA1.x - pointA2.x) * (pointB1.y - pointB2.y) - (pointA1.y - pointA2.y) * (pointB1.x - pointB2.x);
  if (determinate !== 0) {
    let s1 = pointA1.x * pointA2.y - pointA1.y * pointA2.x,
      s2 = pointB1.x * pointB2.y - pointB1.y * pointB2.x;
    let x = (s1 * (pointB1.x - pointB2.x) - (pointA1.x - pointA2.x) * s2) / determinate,
      y = (s1 * (pointB1.y - pointB2.y) - (pointA1.y - pointA2.y) * s2) / determinate;
    let intersect = new google.maps.Point(x, y);
    if (isPointBetween(intersect, pointA1, pointA2) && isPointBetween(intersect, pointB1, pointB2)) {
      intersect = toDecimalDegrees(intersect);
      if (!containsPosition(intersections, intersect)) {
        intersections.push(toLatLng(intersect));
      }
    }
  }
  if (a === countA - 1 && !containsPosition(intersections, a2) && isLocationBetween(positionA2, positionB1, positionB2)) {
    intersections.push(a2);
  }
  if (b === countB - 1 && !containsPosition(intersections, b2) && isLocationBetween(positionB2, positionA1, positionA2)) {
    intersections.push(b2);
  }
  return intersections;
};
export const getShapeTree = shape => {
  let treeVertexSize = 200;
  let tree = [],
    paths = getOverlayPaths(shape).getArray();
  for (let p = 0; p < paths.length; p++) {
    let path = paths[p].getArray();
    for (let i = 0; i < path.length; i += treeVertexSize) {
      let segment = path.slice(i === 0 ? i : i - 1, Math.min(i + treeVertexSize, path.length));
      tree.push({
        segment,
        extent: getPathsBounds(segment)
      });
    }
  }
  return tree;
};
export const getMeasurements = overlays => {
  let measurements = null;
  if (isArray(overlays) && overlays.length > 1) {
    let results = overlays.reduce((results, overlay) => {
      let result = getMeasurements(overlay);
      if (result) {
        Object.entries(result).forEach(([key, value]) => {
          let current = results[key];
          if (isArray(value)) {
            results[key] = current ? distinct([...current, ...value]) : [...value];
          } else {
            results[key] = value + defaultEmpty(current, 0);
          }
        });
      }
      return results;
    }, {});
    if (isObjectNotEmpty(results)) {
      measurements = results;
    }
  } else {
    let overlay = isArray(overlays) ? overlays[0] : overlays;
    let overlayType = getOverlayType(overlay);
    switch (overlayType) {
      case OverlayType.CIRCLE:
        {
          let radius = overlay.getRadius(),
            diameter = radius * 2,
            circumference = diameter * Math.PI,
            //TODO: is this math correct for over a sphere? compare by area of path?
            area = Math.pow(radius, 2) * Math.PI;
          measurements = {
            radius,
            diameter,
            circumference,
            area,
            width: diameter,
            height: diameter,
            perimeter: circumference,
            length: circumference,
            overlayTypes: [overlayType]
          };
          break;
        }
      case OverlayType.RECTANGLE:
        {
          let bounds = overlay.getBounds(),
            path = getBoundsPath(bounds, true),
            width = getBoundsWidth(bounds),
            height = getBoundsHeight(bounds),
            perimeter = google.maps.geometry.spherical.computeLength(path),
            area = google.maps.geometry.spherical.computeArea(path);
          measurements = {
            width,
            height,
            perimeter,
            area,
            length: perimeter,
            overlayTypes: [overlayType]
          };
          break;
        }
      case OverlayType.POLYGON:
        {
          let bounds = getPathsBounds(overlay.getPaths()),
            width = getBoundsWidth(bounds),
            height = getBoundsHeight(bounds),
            area = getPolygonArea(overlay),
            perimeter = getPolygonPerimeter(overlay);
          measurements = {
            width,
            height,
            perimeter,
            area,
            length: perimeter,
            overlayTypes: [overlayType]
          };
          break;
        }
      case OverlayType.POLYLINE:
        {
          let path = overlay.getPath(),
            bounds = getPathsBounds(path),
            width = getBoundsWidth(bounds),
            height = getBoundsHeight(bounds),
            length = google.maps.geometry.spherical.computeLength(path);
          measurements = {
            width,
            height,
            length,
            overlayTypes: [overlayType]
          };
          break;
        }
    }
  }
  return measurements;
};
export const getUnitMeasurements = (overlays, {
  lengthUnitType = LengthUnitType.METER,
  areaUnitType = AreaUnitType.SQUARE_METER,
  precision
} = {}) => {
  let measurements = getMeasurements(overlays);
  if (measurements) {
    Object.entries(measurements).forEach(([key, value]) => {
      if (isNumber(value)) {
        let isArea = key === "area",
          fromUnitType = isArea ? AreaUnitType.SQUARE_METER : LengthUnitType.METER,
          toUnitType = isArea ? areaUnitType : lengthUnitType;
        let convertedValue = convertUnit(value, fromUnitType, toUnitType);
        if (isNumber(precision)) {
          convertedValue = roundTo(convertedValue, precision);
        }
        measurements[key] = {
          value: convertedValue,
          type: toUnitType
        };
      }
    });
  }
  return measurements;
};
export const invertPolygons = polygons => {
  let overlays = toArray(polygons);
  const {
    north,
    south,
    east,
    west
  } = mapExtentProjected;
  const worldPath = [{
    lat: north,
    lng: west
  }, {
    lat: north,
    lng: 0
  }, {
    lat: north,
    lng: east
  }, {
    lat: south,
    lng: east
  }, {
    lat: south,
    lng: 0
  }, {
    lat: south,
    lng: west
  }];
  const paths = [worldPath];
  overlays.forEach(polygon => {
    const polygonPaths = polygon.getPaths().getArray();
    if (polygonPaths.length) {
      const clockwise = isClockwise(polygonPaths[0]);
      polygonPaths.forEach(path => {
        const positions = path.getArray();
        let clone = positions.slice();
        // only reverse positions if necessary
        if (clockwise) {
          clone = clone.reverse();
        }
        paths.push(clone);
      });
    }
  });
  return new google.maps.Polygon({
    paths
  });
};
export const polygonsMask = (polygons, options = {}) => {
  const mask = invertPolygons(polygons);
  mask.setOptions({
    clickable: false,
    zIndex: -1,
    fillColor: "#3a3d40",
    // grey60
    fillOpacity: 0.35,
    strokeWeight: 0,
    ...options
  });
  return mask;
};
export { OverlayType };